import { useRef, useState } from "react";
import { Maximize, Pause, Play, Volume2, VolumeX } from "lucide-react";

const POSTER =
  "https://customer-assets-gfyr7b9c.emergentagent.net/job_duoclair-mvp/artifacts/93f0d6b79a9b85b9_file_0000000041e88210bea86776120c2655.png";

const fmt = (s) => {
  if (!s || Number.isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const VideoPlayer = ({ src }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [failed, setFailed] = useState(false);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => setFailed(true));
    else v.pause();
  };

  const seek = (e) => {
    const v = videoRef.current;
    if (!v || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    v.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  const progress = duration ? (time / duration) * 100 : 0;

  return (
    <div
      className="relative bg-black"
      data-testid="video-player"
    >
      <div className="absolute inset-x-0 top-0 h-px gold-hairline z-20" />
      {!failed ? (
        <video
          ref={videoRef}
          src={src}
          poster={POSTER}
          muted={muted}
          playsInline
          preload="metadata"
          className="w-full aspect-video object-cover cursor-pointer"
          onClick={toggle}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onError={() => setFailed(true)}
          data-testid="video-element"
        />
      ) : (
        <div className="relative w-full aspect-video">
          <img src={POSTER} alt="DuoClair" className="w-full h-full object-cover opacity-60" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <p className="font-serif text-xl sm:text-2xl text-slate-200 italic" data-testid="video-fallback-message">
              Il video sarà disponibile a breve.
            </p>
          </div>
        </div>
      )}

      {!playing && !failed && (
        <button
          onClick={toggle}
          data-testid="video-play-button"
          aria-label="Riproduci il video"
          className="absolute inset-0 z-10 flex items-center justify-center group bg-black/30 transition-colors duration-300 hover:bg-black/20"
        >
          <span className="relative flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 rounded-full border border-gold/50 bg-black/60 backdrop-blur-md transition-transform duration-300 group-hover:scale-110">
            <span className="absolute inset-0 rounded-full border border-gold/30 animate-ping" style={{ animationDuration: "2.5s" }} />
            <Play className="w-8 h-8 sm:w-9 sm:h-9 text-gold fill-gold ml-1" />
          </span>
        </button>
      )}

      {!failed && (
        <div className="absolute inset-x-0 bottom-0 z-20 px-4 pb-3 pt-10 bg-gradient-to-t from-black/90 via-black/40 to-transparent">
          <div
            className="group/bar relative h-1.5 w-full rounded-full bg-white/15 cursor-pointer mb-3"
            onClick={seek}
            data-testid="video-progress-bar"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-gold-dark via-gold to-gold-light"
              style={{ width: `${progress}%` }}
            />
            <div
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-gold-light shadow-[0_0_12px_rgba(212,160,60,0.8)] opacity-0 group-hover/bar:opacity-100 transition-opacity"
              style={{ left: `${progress}%` }}
            />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={toggle} data-testid="video-toggle-button" aria-label={playing ? "Pausa" : "Play"} className="text-slate-200 hover:text-gold transition-colors">
                {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 fill-current" />}
              </button>
              <button
                onClick={() => {
                  setMuted(!muted);
                  if (videoRef.current) videoRef.current.muted = !muted;
                }}
                data-testid="video-mute-button"
                aria-label={muted ? "Attiva audio" : "Silenzia"}
                className="text-slate-200 hover:text-gold transition-colors"
              >
                {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <span className="font-mono text-[11px] text-zinc-400 tabular-nums" data-testid="video-time">
                {fmt(time)} <span className="text-zinc-600">/ {fmt(duration) || "1:00"}</span>
              </span>
            </div>
            <button
              onClick={() => videoRef.current?.requestFullscreen?.()}
              data-testid="video-fullscreen-button"
              aria-label="Schermo intero"
              className="text-slate-200 hover:text-gold transition-colors"
            >
              <Maximize className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
